import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";

const StatCard: React.FC<{
  type: string;
  value: string;
  title: string;
  description: string;
  index: number;
  isActive: boolean;
}> = ({ type, value, title, description, index, isActive }) => {
  return (
    <motion.div
      initial={{ opacity: 0.6, y: 0, scale: 0.95 }}
      animate={{
        opacity: isActive ? 1 : 0.6,
        y: 0,
        scale: isActive ? 1 : 0.95,
      }}
      transition={{ duration: 0.5 }}
      style={{
        minWidth: "300px",
        margin: "0 12px",
        transition: "all 0.3s ease",
      }}
    >
      <Box
        sx={{
          p: 3,
          height: "100%",
          background: "rgba(255, 255, 255, 0.8)",
          border: "1px solid rgba(6, 182, 212, 0.1)",
          borderRadius: "16px",
          backdropFilter: "blur(10px)",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "2px",
            background: "linear-gradient(90deg, #06B6D4, #22D3EE)",
            opacity: isActive ? 0.8 : 0.3,
            transition: "opacity 0.3s ease",
          },
          "&:hover": {
            background: "rgba(255, 255, 255, 0.9)",
            borderColor: "rgba(6, 182, 212, 0.2)",
            transform: "translateY(-4px)",
            boxShadow: "0 8px 24px rgba(6, 182, 212, 0.1)",
          },
          transition: "all 0.3s ease",
        }}
      >
        <Typography
          variant="h2"
          sx={{
            color: "#06B6D4",
            fontWeight: 700,
            fontFamily: "Montserrat",
            fontSize: "2.5rem",
            lineHeight: 1.2,
            background: "linear-gradient(135deg, #06B6D4 0%, #22D3EE 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            color: "#1A2027",
            fontWeight: 600,
            fontFamily: "Montserrat",
            fontSize: "1.25rem",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: "#4A5568",
            fontFamily: "Montserrat",
            lineHeight: 1.6,
            fontSize: "0.875rem",
          }}
        >
          {description}
        </Typography>
      </Box>
    </motion.div>
  );
};

const ProgressIndicator: React.FC<{ currentIndex: number; total: number }> = ({
  currentIndex,
  total,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 1,
        mt: 4,
      }}
    >
      {Array.from({ length: total }).map((_, index) => (
        <motion.div
          key={index}
          initial={false}
          animate={{
            scale: currentIndex === index ? 1 : 0.8,
            opacity: currentIndex === index ? 1 : 0.3,
          }}
          transition={{ duration: 0.3 }}
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: "linear-gradient(90deg, #06B6D4, #22D3EE)",
            cursor: "pointer",
          }}
        />
      ))}
    </Box>
  );
};

export const StatsSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const carouselRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const stats = [
    {
      type: "inventory",
      value: "98%",
      title: "Inventory Accuracy",
      description:
        "Thanks to batch-level tracking and automated stock deduction from recipes and sales.",
    },
    {
      type: "costing",
      value: "3x",
      title: "Faster Recipe Costing",
      description:
        "Real-time ingredient cost updates and expiry alerts make costing effortless and reliable.",
    },
    {
      type: "menu",
      value: "100%",
      title: "Custom Menu Engineering",
      description:
        "Craft APIs and sellable items using your own BoM & menu templates—no coding, no limits.",
    },
    {
      type: "savings",
      value: "20%",
      title: "Food Cost Savings",
      description:
        "Smart stock usage suggestions and supplier optimization reduce waste and overbuying.",
    },
    {
      type: "planning",
      value: "35%",
      title: "Efficient Purchase Planning",
      description:
        "From raw material planning to recipe-ready batches—every gram is accounted for.",
    },
    {
      type: "branch",
      title: "Multi-Branch Control",
      value: "100%",
      description:
        "Assign access per department, warehouse, or branch—total control, zero confusion.",
    },
    {
      type: "visibility",
      value: "360°",
      title: "Operational Visibility",
      description:
        "From kitchen to cashier, track activity logs, costs, sales, and stock—all in one place.",
    },
    {
      type: "pos",
      value: "100%",
      title: "POS Integration",
      description:
        "Seamless cashier-side integration with backend inventory and menu flow.",
    },
  ];

  // Create duplicated stats array for infinite scrolling
  const allStats = [...stats, ...stats];

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setActiveIndex((prevIndex) => (prevIndex + 1) % stats.length);
          return 0;
        }
        return prev + 0.2; // for 5-second duration (100 / 5 seconds = 20% per second)
      });
    }, 10);

    return () => clearInterval(interval);
  }, [stats.length]);

  // Scroll the carousel when active index changes
  useEffect(() => {
    if (!carouselRef.current) return;

    // Calculate position for infinite scrolling
    const cardWidth = 324; // 300px + 24px margin
    const scrollPosition = (activeIndex % stats.length) * cardWidth;

    carouselRef.current.scrollTo({
      left: scrollPosition,
      behavior: "smooth",
    });
  }, [activeIndex, stats.length]);

  // Generate visible indices for highlighting active cards
  const getVisibleIndices = () => {
    const indices = [];
    const visibleCount = isMobile ? 1 : 3;

    for (let i = 0; i < visibleCount; i++) {
      const index = (activeIndex + i) % stats.length;
      indices.push(index);
    }

    return indices;
  };

  const visibleIndices = getVisibleIndices();

  return (
    <Box
      sx={{
        py: { xs: 10, md: 15 },
        background: "linear-gradient(135deg, #F7FAFC 0%, #EDF2F7 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 50% 50%, rgba(6, 182, 212, 0.03) 0%, transparent 70%)",
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: "center",
              mb: 3,
              color: "#1A2027",
              fontWeight: 700,
              fontFamily: "Montserrat",
              fontSize: { xs: "2rem", md: "2.75rem" },
              maxWidth: "800px",
              mx: "auto",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: -16,
                left: "50%",
                transform: "translateX(-50%)",
                width: "80px",
                height: "3px",
                background: "linear-gradient(90deg, #06B6D4, #22D3EE)",
                borderRadius: "2px",
              },
            }}
          >
            Key Performance Metrics
          </Typography>
          <Typography
            sx={{
              textAlign: "center",
              mb: 8,
              color: "#4A5568",
              maxWidth: "700px",
              mx: "auto",
              fontFamily: "Montserrat",
              fontSize: { xs: "1rem", md: "1.125rem" },
              lineHeight: 1.6,
              mt: 4,
            }}
          >
            Discover how our solution drives measurable business outcomes
            through advanced analytics and seamless integration
          </Typography>
        </motion.div>

        <Box sx={{ position: "relative", width: "100%", overflow: "hidden" }}>
          <Box
            ref={carouselRef}
            sx={{
              display: "flex",
              width: "100%",
              overflowX: "hidden",
              py: 4,
              px: { xs: 2, md: 4 },
            }}
          >
            {/* Render duplicated stats for infinite scrolling */}
            {allStats.map((stat, index) => {
              const originalIndex = index % stats.length;
              return (
                <StatCard
                  key={index}
                  {...stat}
                  index={originalIndex}
                  isActive={visibleIndices.includes(originalIndex)}
                />
              );
            })}
          </Box>
          <ProgressIndicator currentIndex={activeIndex} total={stats.length} />
        </Box>
      </Container>
    </Box>
  );
};
